// OnboardingGuard ViewModel - determines if user should be redirected to onboarding
// Part of ViewModel layer - presentation logic for navigation decisions
// Uses lightweight queries to minimize load time on guarded pages

import {
  useGetOnboardingResponsesQuery,
  useGetOnboardingDismissalQuery,
  useGetStrategiesQuery,
} from 'generated/graphql';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import { isEligibleForOnboarding } from '../model/onboardingEligibility';
import {
  isDismissedOnAnyPlatform,
  toOnboardingDismissal,
} from '../model/Onboarding.mappers';

type ShouldRedirectInput = {
  isEligibleUser: boolean;
  hasResponses: boolean;
  hasSelectedStrategy: boolean;
  isDismissed: boolean;
};

export const shouldRedirectToOnboarding = ({
  isEligibleUser,
  hasResponses,
  hasSelectedStrategy,
  isDismissed,
}: ShouldRedirectInput): boolean => {
  return (
    isEligibleUser && !hasResponses && !isDismissed && !hasSelectedStrategy
  );
};

// Guard result type
export type OnboardingGuardVM = {
  shouldRedirectToOnboarding: boolean;
  isEligibleUser: boolean;
  hasResponses: boolean;
  isDismissed: boolean;
  hasSelectedStrategy: boolean;
};

// Guard hook - lightweight check for redirect decision
// Only fetches responses and dismissal (not full onboarding data)
export const useOnboardingGuard = (
  creatorId: number | null,
  creatorCreatedAt: Date | string | null,
): Async<OnboardingGuardVM> => {
  const skip = !creatorId;

  // Only fetch responses count - lightweight query
  const responsesQuery = useGetOnboardingResponsesQuery({
    variables: { creatorId: String(creatorId ?? '') },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Only fetch dismissal state - use cache-and-network to get fresh data
  // while still showing cached data immediately
  const dismissalQuery = useGetOnboardingDismissalQuery({
    variables: { creatorId: String(creatorId ?? '') },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch strategies to check if user has already selected one
  const strategiesQuery = useGetStrategiesQuery({
    variables: { creatorId: String(creatorId ?? '') },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Handle loading states
  if (
    responsesQuery.loading ||
    dismissalQuery.loading ||
    strategiesQuery.loading
  ) {
    return { type: 'fetching' };
  }

  // Handle errors
  if (responsesQuery.error) {
    return { type: 'fetch-error', value: responsesQuery.error.message };
  }
  if (dismissalQuery.error) {
    return { type: 'fetch-error', value: dismissalQuery.error.message };
  }
  if (strategiesQuery.error) {
    return { type: 'fetch-error', value: strategiesQuery.error.message };
  }

  // Handle skip case (no creatorId)
  if (skip) {
    return toAsyncFetched({
      shouldRedirectToOnboarding: false,
      isEligibleUser: false,
      hasResponses: false,
      isDismissed: false,
      hasSelectedStrategy: false,
    });
  }

  // Check eligibility based on whether creator was live at launch
  const isEligibleUser = isEligibleForOnboarding(creatorCreatedAt);

  // Check if user has any responses
  const responses = responsesQuery.data?.GetOnboardingResponses ?? [];
  const hasResponses = responses.length > 0;

  const dismissal = toOnboardingDismissal(
    dismissalQuery.data?.GetOnboardingDismissal,
  );
  const isDismissed = isDismissedOnAnyPlatform(dismissal);

  // Check if user has already selected a strategy
  const hasSelectedStrategy =
    strategiesQuery.data?.GetStrategies?.some((s) => s.isSelected) ?? false;

  // Determine if should redirect:
  // - User is eligible (not live before launch)
  // - Has no responses (hasn't started or completed onboarding)
  // - Hasn't dismissed onboarding
  // - Hasn't already selected a strategy (via "Skip for now" flow)
  const shouldRedirectToOnboardingResult = shouldRedirectToOnboarding({
    isEligibleUser,
    hasResponses,
    hasSelectedStrategy,
    isDismissed,
  });

  return toAsyncFetched({
    shouldRedirectToOnboarding: shouldRedirectToOnboardingResult,
    isEligibleUser,
    hasResponses,
    isDismissed,
    hasSelectedStrategy,
  });
};
